import { Link } from "react-router-dom";
import { 
  GraduationCap, 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Facebook,
  Twitter,
  Instagram,
  Youtube,
  Send
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const quickLinks = [
  { name: "About Us", path: "/about" },
  { name: "Academics", path: "/academics" },
  { name: "Admission", path: "/admission" },
  { name: "Facilities", path: "/facilities" },
  { name: "Gallery", path: "/gallery" },
  { name: "Contact", path: "/contact" },
];

const academicLinks = [
  { name: "Pre Primary", path: "/academics#preprimary" },
  { name: "Primary School", path: "/academics#primary" },
  { name: "Middle School", path: "/academics#middle" },
  { name: "High School", path: "/academics#high" },
];

export const Footer = () => {
  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    alert("Thank you for subscribing to our newsletter!");
  };

  return (
    <footer className="bg-primary text-white" data-testid="main-footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* School Info */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                <GraduationCap className="text-primary" size={28} />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold">ID4U School</h3>
                <p className="text-xs text-white/70">Excellence in Education</p>
              </div>
            </Link>
            <p className="text-white/80 text-sm leading-relaxed mb-6">
              ID4U School is committed to providing quality education that nurtures young minds 
              and prepares students for a bright future. We focus on holistic development through 
              modern teaching methods.
            </p>
            <div className="flex gap-3">
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
                aria-label="Twitter"
              >
                <Twitter size={18} />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a 
                href="https://youtube.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
                aria-label="Youtube"
              >
                <Youtube size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-white/80 hover:text-secondary transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Academics */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6">Academics</h4>
            <ul className="space-y-3">
              {academicLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-white/80 hover:text-secondary transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Newsletter */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-4 mb-6">
              <li className="flex items-start gap-3 text-sm text-white/80">
                <MapPin size={18} className="mt-0.5 flex-shrink-0 text-secondary" />
                <span>123 Education Lane, Knowledge City, State - 500001</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-white/80">
                <Phone size={18} className="flex-shrink-0 text-secondary" />
                <a href="tel:+919281410305" className="hover:text-secondary transition-colors">
                  +91 9281410305
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-white/80">
                <Mail size={18} className="flex-shrink-0 text-secondary" />
                <a href="mailto:info@id4uschool.com" className="hover:text-secondary transition-colors">
                  info@id4uschool.com
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-white/80">
                <Clock size={18} className="flex-shrink-0 text-secondary" />
                <span>Mon - Sat: 8:00 AM - 4:00 PM</span>
              </li>
            </ul>

            {/* Newsletter */}
            <h5 className="font-medium mb-3">Newsletter</h5>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
              <Input
                type="email"
                placeholder="Your email"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 flex-1"
                required
              />
              <Button type="submit" className="bg-secondary hover:bg-secondary/90 px-3">
                <Send size={18} />
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/70">
            <p>© {new Date().getFullYear()} ID4U School. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="hover:text-secondary transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-secondary transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
